import { useMemo } from "react";
import { phaseColor, RACI_ROLES } from "@/lib/constants";
import { Users, Info } from "lucide-react";

const ROLE_BY_KEY = Object.fromEntries(RACI_ROLES.map((r) => [r.key, r]));
const splitPeople = (v) => (v || "").split(",").map((s) => s.trim()).filter(Boolean);

export default function RaciView({ tasks, phases, phaseColors, onOpenTask }) {
  const colorOf = (p) => (phaseColors && phaseColors[p]) || phaseColor(p);
  const phaseList = phases && phases.length ? phases : [...new Set(tasks.map((t) => t.phase))];

  // Collect people and each task's role map { person: [roleKeys] }
  const { people, roleMap } = useMemo(() => {
    const set = new Set();
    const map = {};
    tasks.forEach((t) => {
      const roles = {};
      const add = (person, key) => {
        if (!person) return;
        set.add(person);
        roles[person] = roles[person] ? [...new Set([...roles[person], key])] : [key];
      };
      const resp = t.responsible?.trim() || t.assignee?.trim();
      add(resp, "responsible");
      add(t.accountable?.trim(), "accountable");
      splitPeople(t.consulted).forEach((p) => add(p, "consulted"));
      splitPeople(t.informed).forEach((p) => add(p, "informed"));
      map[t.id] = roles;
    });
    return { people: Array.from(set).sort(), roleMap: map };
  }, [tasks]);

  return (
    <div className="space-y-8" data-testid="raci-view">
      {/* Definitions */}
      <div className="border border-border rounded-lg p-5 bg-secondary/20" data-testid="raci-definitions">
        <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-1"><Info className="h-5 w-5 text-primary" /> RACI roles (industry standard)</h3>
        <p className="text-xs text-muted-foreground mb-4">A responsibility assignment matrix. Assign exactly one <strong>Accountable</strong> per task to avoid ambiguity.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {RACI_ROLES.map((r) => (
            <div key={r.key} className="flex gap-3">
              <span className="h-7 w-7 rounded-md text-white text-sm font-bold flex items-center justify-center shrink-0" style={{ background: r.color }}>{r.letter}</span>
              <div>
                <p className="text-sm font-semibold">{r.label}</p>
                <p className="text-xs text-muted-foreground leading-snug">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-4 pt-3 border-t border-border">Some organizations use <strong>RASCI</strong>, adding an <strong>S</strong> for Support (or Stakeholder) to indicate additional roles in the process.</p>
      </div>

      {/* RACI matrix */}
      {people.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center" data-testid="raci-empty">
          <Users className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No RACI roles assigned yet. Open a task and set Responsible, Accountable, Consulted, or Informed.</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="text-left px-4 py-3 font-semibold sticky left-0 bg-secondary/40 min-w-[220px]">
                  <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> Task</span>
                </th>
                {people.map((p) => (
                  <th key={p} className="px-3 py-3 text-center font-semibold whitespace-nowrap">{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {phaseList.map((phase) => {
                const items = tasks.filter((t) => t.phase === phase);
                if (!items.length) return null;
                return (
                  <>
                    <tr key={`ph-${phase}`} className="bg-secondary/20">
                      <td colSpan={people.length + 1} className="px-4 py-2 text-[11px] font-bold uppercase tracking-wide" style={{ color: colorOf(phase) }}>{phase}</td>
                    </tr>
                    {items.map((t) => (
                      <tr key={t.id} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`raci-row-${t.id}`}>
                        <td className="px-4 py-2.5 font-medium sticky left-0 bg-background">
                          <button onClick={() => onOpenTask?.(t)} className="text-left hover:text-primary truncate block max-w-[200px]" data-testid={`raci-task-${t.id}`}>{t.title}</button>
                        </td>
                        {people.map((p) => {
                          const roles = roleMap[t.id]?.[p] || [];
                          return (
                            <td key={p} className="px-3 py-2.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {roles.map((rk) => {
                                  const r = ROLE_BY_KEY[rk];
                                  return <span key={rk} title={r.label} className="h-6 w-6 rounded text-white text-xs font-bold flex items-center justify-center" style={{ background: r.color }}>{r.letter}</span>;
                                })}
                                {roles.length === 0 && <span className="text-muted-foreground/40">·</span>}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
